import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from './useAuth';
import { toast } from '@/hooks/use-toast';

export interface Guest {
  id: string;
  invitation_id: string;
  guest_name: string;
  guest_email: string | null;
  guest_phone: string | null;
  group_type: string;
  table_number: number | null;
  passes_count: number;
  rsvp_status: 'pending' | 'confirmed' | 'declined';
  rsvp_guests_count: number;
  rsvp_dietary_restrictions: string | null;
  rsvp_message: string | null;
  rsvp_responded_at?: string | null;
  unique_access_code: string;
  created_at: string;
  updated_at: string;
  // 008 — aditivos
  display_label?: string | null;         // US4: rótulo (NULL → usa guest_name)
  link_sent_at?: string | null;          // US3: "enviado"
  first_viewed_at?: string | null;       // US7: primera apertura
  last_viewed_at?: string | null;        // US7: última apertura
  view_count?: number;                   // US7
  total_view_seconds?: number;           // US7 best-effort
  max_section_viewed?: string | null;    // US7 best-effort
  rsvp_last_source?: 'guest' | 'couple' | null; // US1/US5: origen del último cambio
  rsvp_last_edited_at?: string | null;   // US1
}

// Fetch all guests across all user's invitations
export const useAllGuests = () => {
  const { user } = useAuth();
  
  return useQuery({
    queryKey: ['all-guests', user?.id],
    queryFn: async () => {
      if (!user) return [];
      
      // First get user's invitations
      const { data: invitations, error: invError } = await supabase
        .from('wedding_invitations')
        .select('id, bride_name, groom_name, unique_slug, is_published')
        .eq('user_id', user.id);
      
      if (invError) throw invError;
      if (!invitations || invitations.length === 0) return [];
      
      const invitationIds = invitations.map(inv => inv.id);
      
      // Then get all guests for those invitations
      const { data: guests, error: guestError } = await supabase
        .from('invitation_guests')
        .select('*')
        .in('invitation_id', invitationIds)
        .order('created_at', { ascending: false });
      
      if (guestError) throw guestError;
      
      return (guests || []).map(g => {
        const inv = invitations.find(i => i.id === g.invitation_id);
        return {
          ...g,
          invitationName: inv ? `${inv.bride_name} & ${inv.groom_name}` : 'Sin invitación',
          invitationSlug: inv?.unique_slug || '',
          invitationPublished: inv?.is_published || false,
        };
      }) as (Guest & { invitationName: string; invitationSlug: string; invitationPublished: boolean })[];
    },
    enabled: !!user,
  });
};

// Create guest
export const useCreateGuest = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (guest: {
      invitation_id: string;
      guest_name: string;
      display_label?: string | null;
      guest_email?: string;
      guest_phone?: string;
      group_type: string;
      table_number?: number;
      passes_count: number;
      rsvp_status: string;
    }) => {
      // 008 · US2 — el código de acceso lo genera la BD (generate_access_code, CSPRNG).
      const { data, error } = await supabase
        .from('invitation_guests')
        .insert({ ...guest })
        .select()
        .single();
      
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['all-guests'] });
      toast({ title: 'Invitado agregado' });
    },
    onError: (error) => {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    },
  });
};

// 008 · US3 — marcar "enviado" (link_sent_at) sin toast, para las acciones de compartir.
export const useMarkLinkSent = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('invitation_guests')
        .update({ link_sent_at: new Date().toISOString() })
        .eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['all-guests'] });
      queryClient.invalidateQueries({ queryKey: ['invitation-guests'] });
    },
  });
};

// Update guest
export const useUpdateGuest = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ id, ...updates }: Partial<Guest> & { id: string }) => {
      const { data, error } = await supabase
        .from('invitation_guests')
        .update(updates)
        .eq('id', id)
        .select()
        .single();
      
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['all-guests'] });
      toast({ title: 'Invitado actualizado' });
    },
    onError: (error) => {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    },
  });
};

export interface BulkInsertGuestRow {
  guest_name: string;
  guest_phone: string;
  passes_count: number;
  group_type: string;
  display_label?: string | null;
}

export interface BulkInsertGuestsResult {
  imported: number;
  duplicates: number;
  errors: number;
}

// Bulk insert guests with application-side dedup by (invitation_id, guest_phone).
// invitation_guests has no unique index on (invitation_id, guest_phone), so we
// fetch existing phones for the invitation and skip rows that collide either
// with the DB or with earlier rows in the same batch.
export const useBulkInsertGuests = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: {
      invitation_id: string;
      rows: BulkInsertGuestRow[];
    }): Promise<BulkInsertGuestsResult> => {
      const { invitation_id, rows } = input;
      if (rows.length === 0) {
        return { imported: 0, duplicates: 0, errors: 0 };
      }

      const { data: existing, error: fetchError } = await supabase
        .from('invitation_guests')
        .select('guest_phone')
        .eq('invitation_id', invitation_id);

      if (fetchError) throw fetchError;

      const existingPhones = new Set(
        (existing ?? []).map((g) => g.guest_phone).filter(Boolean) as string[],
      );

      const seenInBatch = new Set<string>();
      // 008 · US2 — unique_access_code lo asigna la BD (generate_access_code).
      const toInsert: Array<BulkInsertGuestRow & {
        invitation_id: string;
        rsvp_status: 'pending';
      }> = [];
      let duplicates = 0;

      for (const row of rows) {
        if (existingPhones.has(row.guest_phone) || seenInBatch.has(row.guest_phone)) {
          duplicates += 1;
          continue;
        }
        seenInBatch.add(row.guest_phone);
        toInsert.push({
          ...row,
          invitation_id,
          rsvp_status: 'pending',
        });
      }

      let imported = 0;
      let errors = 0;

      if (toInsert.length > 0) {
        const { data, error } = await supabase
          .from('invitation_guests')
          .insert(toInsert)
          .select('id');

        if (error) {
          errors = toInsert.length;
        } else {
          imported = data?.length ?? toInsert.length;
        }
      }

      return { imported, duplicates, errors };
    },
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ['all-guests'] });
      queryClient.invalidateQueries({ queryKey: ['invitation-guests'] });
      const parts: string[] = [`${result.imported} invitados importados`];
      if (result.duplicates) parts.push(`${result.duplicates} duplicados ignorados`);
      if (result.errors) parts.push(`${result.errors} con errores`);
      toast({ title: 'Import completado', description: parts.join(', ') });
    },
    onError: (error: Error) => {
      toast({ title: 'Error al importar', description: error.message, variant: 'destructive' });
    },
  });
};

// Delete guest
export const useDeleteGuest = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('invitation_guests')
        .delete()
        .eq('id', id);
      
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['all-guests'] });
      toast({ title: 'Invitado eliminado' });
    },
    onError: (error) => {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    },
  });
};
